/**
 * GrantEditor — one slide-over form used for BOTH granting (new person) and
 * editing (existing person) Cockpit access.
 *
 * Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09): a
 * grant is a set of accessSlug checkboxes, no levels. A separate "Super admin"
 * checkbox at the top toggles the person's super-admin flag; while checked,
 * every per-tool checkbox below is disabled (greyed) because super-admin
 * bypasses all of them.
 *
 * Person:
 *   - Grant mode shows a debounced directory search (GET /v1/access/directory,
 *     min 2 chars) at the top; picking a user reveals the form body.
 *   - Edit mode receives the person and shows their name/email fixed (no search).
 *
 * Pre-populate: once a person is selected (picked in Grant mode, or passed in
 * Edit mode), the checked tools and the Super admin checkbox are pre-filled
 * from that person's EXISTING grant, sourced from the already-loaded
 * useAccessGrants() list by matching principal_ref to the selected user's id
 * (no extra endpoint).
 *
 * Layout mirrors the Cockpit menu: grantable tools are grouped into collapsible
 * department sections (catalog department + tool names, resolved through
 * accessScopes.ts). Each section header carries a "Select all" / "Clear all"
 * link that toggles every tool in that section at once.
 *
 * Save: the api's grant writes are per-slug and incremental (confirmed against
 * the api rewrite 2026-08-09), not a full-set replacement, so two admins
 * editing the same person cannot clobber each other's unrelated changes. Save
 * diffs the checked set against the person's existing grant and issues one
 * PUT /v1/access/grants/:principalRef/:toolSlug per newly-checked tool and one
 * DELETE .../:toolSlug per newly-unchecked tool, all in parallel. If the Super
 * admin checkbox changed, it also fires PUT (turn on) or DELETE (turn off)
 * /v1/access/superadmin/:principalRef, no body either way.
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Loader2, X } from 'lucide-react';
import {
  useAccessGrants,
  useDirectorySearch,
  useGrantTool,
  useRevokeTool,
  useGrantSuperAdmin,
  useRevokeSuperAdmin,
  useCreateUser,
  type AccessGrant,
  type DirectoryUser,
} from '@/lib/useAccess';
import {
  buildGrantableDepts,
  type GrantableDept,
  type GrantableEntry,
} from '@/lib/accessScopes';

type Props = {
  mode: 'new' | 'edit';
  grant?: AccessGrant;
  onClose: () => void;
};

// A well-formed email typed in full is itself a valid principal to grant to,
// even if it matches nobody in the Entra directory or the grants list.
function isEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export function GrantEditor({ mode, grant, onClose }: Props) {
  const grantsQ = useAccessGrants();
  const grantToolM = useGrantTool();
  const revokeToolM = useRevokeTool();
  const grantSuperAdminM = useGrantSuperAdmin();
  const revokeSuperAdminM = useRevokeSuperAdmin();

  // Selected person. Edit: fixed from the grant. Grant: chosen from search.
  const [picked, setPicked] = useState<DirectoryUser | null>(
    mode === 'edit' && grant
      ? {
          id: grant.principal_ref,
          displayName: grant.name ?? grant.email ?? grant.principal_ref,
          mail: grant.email,
        }
      : null,
  );

  // Directory search (Grant mode only).
  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(searchInput), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);
  const dirQ = useDirectorySearch(mode === 'new' ? debounced : '');

  // Search results shown in the picker (Grant mode). The directory endpoint
  // only ever returns Microsoft/Entra users, so external people, consultants,
  // and Google Workspace store accounts never appear there. Merge in anyone who
  // already has access (from the loaded grants list) whose name or email
  // matches, and accept a raw well-formed email as a selectable principal, so a
  // super-admin can start managing any access-holder, not only Entra users.
  const searchResults = useMemo<DirectoryUser[]>(() => {
    if (mode !== 'new') return [];
    const needle = debounced.trim().toLowerCase();
    if (needle.length < 2) return [];

    const results: DirectoryUser[] = [...dirQ.users];
    const seenIds = new Set(results.map((u) => u.id));
    const seenMails = new Set(
      results.map((u) => u.mail?.toLowerCase()).filter(Boolean) as string[],
    );

    for (const g of grantsQ.grants) {
      const hay = `${g.name ?? ''} ${g.email ?? ''} ${g.principal_ref}`.toLowerCase();
      if (!hay.includes(needle)) continue;
      if (seenIds.has(g.principal_ref)) continue;
      if (g.email && seenMails.has(g.email.toLowerCase())) continue;
      results.push({
        id: g.principal_ref,
        displayName: g.name ?? g.email ?? g.principal_ref,
        mail: g.email,
      });
      seenIds.add(g.principal_ref);
      if (g.email) seenMails.add(g.email.toLowerCase());
    }

    const typed = debounced.trim();
    if (isEmail(typed) && !seenMails.has(typed.toLowerCase()) && !seenIds.has(typed)) {
      results.push({ id: typed, displayName: typed, mail: typed });
    }

    return results;
  }, [mode, debounced, dirQ.users, grantsQ.grants]);

  // The existing grant for the selected person, found in the loaded grants
  // list. In Edit mode this is the passed grant; in Grant mode it is whichever
  // grant matches the picked user's id (often none). Match on EMAIL as well as
  // id: many grants (external people, Google Workspace store accounts, older
  // rows) are keyed by email, while the Entra picker returns an OID as id, so
  // an id-only match would miss them and Save would orphan their real grants.
  const existingGrant = useMemo<AccessGrant | undefined>(() => {
    if (!picked) return undefined;
    return grantsQ.grants.find(
      (g) =>
        g.principal_ref === picked.id ||
        (!!picked.mail && g.email?.toLowerCase() === picked.mail.toLowerCase()),
    );
  }, [picked, grantsQ.grants]);

  // Checked accessSlugs.
  const [tools, setTools] = useState<Set<string>>(new Set());
  // Super admin checkbox. Disables (greys) every tool checkbox below while on.
  const [superAdmin, setSuperAdmin] = useState(false);

  // (Re)seed from the selected person's existing grant whenever the person
  // changes or the grants finish loading.
  useEffect(() => {
    setTools(new Set(existingGrant?.tools ?? []));
    setSuperAdmin(existingGrant?.super_admin ?? false);
  }, [existingGrant]);

  const [error, setError] = useState<string | null>(null);
  const saving =
    grantToolM.isPending ||
    revokeToolM.isPending ||
    grantSuperAdminM.isPending ||
    revokeSuperAdminM.isPending;

  // Escape closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSave = !!picked && !saving;

  const onSave = async () => {
    if (!picked) return;
    setError(null);
    try {
      // Write back to the person's existing principal_ref when one was matched
      // (often an EMAIL for non-Entra or older grants), never a fresh OID, so
      // their real grants are edited in place instead of being orphaned under a
      // new duplicate identity.
      const principalRef = existingGrant?.principal_ref ?? picked.id;
      const meta = { email: picked.mail ?? undefined, name: picked.displayName };
      const before = new Set(existingGrant?.tools ?? []);

      // Diff the checked set against what the person already has: one PUT per
      // newly-checked tool, one DELETE per newly-unchecked tool. Untouched
      // tools get no call at all, so this can never clobber a grant another
      // admin made to a tool this editor didn't touch.
      const toGrant = [...tools].filter((slug) => !before.has(slug));
      const toRevoke = [...before].filter((slug) => !tools.has(slug));

      const calls: Promise<unknown>[] = [
        ...toGrant.map((toolSlug) => grantToolM.mutateAsync({ principalRef, toolSlug, meta })),
        ...toRevoke.map((toolSlug) => revokeToolM.mutateAsync({ principalRef, toolSlug })),
      ];

      const superAdminBefore = existingGrant?.super_admin ?? false;
      if (superAdmin !== superAdminBefore) {
        calls.push(
          superAdmin
            ? grantSuperAdminM.mutateAsync({ principalRef })
            : revokeSuperAdminM.mutateAsync({ principalRef }),
        );
      }

      await Promise.all(calls);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'new' ? 'Grant access' : 'Edit access'}
        className="relative z-10 flex h-full w-full max-w-md flex-col bg-amp-white shadow-2 dark:bg-amp-dark-surface"
      >
        <div className="flex items-center justify-between border-b border-amp-line px-5 py-4 dark:border-amp-dark-line">
          <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">
            {mode === 'new' ? 'Grant access' : 'Edit access'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Person */}
          <div className="mb-5">
            <p className="text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2">
              Person
            </p>
            {mode === 'edit' ? (
              <div className="rounded border border-amp-line px-3 py-2 dark:border-amp-dark-line">
                <div className="text-sm text-amp-ink dark:text-amp-dark-ink">{picked?.displayName}</div>
                {picked?.mail && (
                  <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{picked.mail}</div>
                )}
              </div>
            ) : picked ? (
              <div className="flex items-center justify-between rounded border border-amp-line px-3 py-2 dark:border-amp-dark-line">
                <div>
                  <div className="text-sm text-amp-ink dark:text-amp-dark-ink">{picked.displayName}</div>
                  {picked.mail && (
                    <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{picked.mail}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="text-xs text-amp-olive hover:underline dark:text-amp-olive-dark"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name or email (min 2 characters)..."
                  className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
                />
                {debounced.trim().length >= 2 && (
                  <div className="mt-1 max-h-60 overflow-y-auto rounded border border-amp-line dark:border-amp-dark-line">
                    {dirQ.isLoading && searchResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        Searching...
                      </div>
                    ) : searchResults.length === 0 ? (
                      dirQ.error ? (
                        <div className="px-3 py-2 text-xs text-red-700 dark:text-red-300">
                          {dirQ.error.message}
                        </div>
                      ) : (
                        <div className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          No matches.
                        </div>
                      )
                    ) : (
                      searchResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setPicked(u)}
                          className="block w-full text-left px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                        >
                          <span className="block">{u.displayName}</span>
                          {u.mail && (
                            <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                              {u.mail}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tool access, only once a person is selected. */}
          {picked && (
            <div>
              <label className="mb-3 flex items-start gap-2 rounded border border-amp-line bg-amp-cream-warm px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
                <input
                  type="checkbox"
                  checked={superAdmin}
                  onChange={(e) => setSuperAdmin(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm text-amp-ink dark:text-amp-dark-ink">
                  <span className="font-medium">Super admin.</span> Every tool below, with no
                  per-tool grants needed. While checked, the tools below have no effect.
                </span>
              </label>

              <p className="text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2">
                Tool access
              </p>

              <div className="mb-3 rounded border border-amp-line bg-amp-cream/40 px-3 py-2 text-xs text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface-warm/40 dark:text-amp-dark-ink-muted">
                A checked tool is granted. An unchecked tool is not.
              </div>

              <ToolGrid value={tools} onChange={setTools} disabled={superAdmin} />
            </div>
          )}

          {error && (
            <div className="mt-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-amp-line px-5 py-4 dark:border-amp-dark-line">
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-3">
            Changes take effect at the user's next sign-in. All changes are logged.
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className="inline-flex items-center gap-1 text-sm rounded bg-amp-olive-deep px-3 py-1.5 font-medium text-amp-cream hover:bg-amp-olive disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amp-olive dark:hover:bg-amp-olive-deep"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Reusable tool-checkbox grid ------------------------------------------

/**
 * The grantable-tool checkbox grid, grouped into collapsible department
 * sections. Extracted from GrantEditor so the Add-user form reuses the exact
 * same grid and styling. Controlled: it holds no state, it reads the checked
 * set from `value` and reports every change through `onChange`.
 */
export function ToolGrid({
  value,
  onChange,
  disabled = false,
}: {
  value: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}) {
  const grantableDepts: GrantableDept[] = useMemo(() => buildGrantableDepts(), []);

  const toggleTool = (slug: string, checked: boolean) => {
    const next = new Set(value);
    if (checked) next.add(slug);
    else next.delete(slug);
    onChange(next);
  };

  // Check or clear every grantable tool in a department at once.
  const setDeptAll = (dept: GrantableDept, checked: boolean) => {
    const next = new Set(value);
    for (const entry of dept.entries) {
      if (!entry.grantable) continue;
      if (checked) next.add(entry.accessSlug);
      else next.delete(entry.accessSlug);
    }
    onChange(next);
  };

  if (grantableDepts.length === 0) {
    return (
      <div className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        No grantable tools.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {grantableDepts.map((dept) => (
        <DeptSection
          key={dept.deptId}
          dept={dept}
          tools={value}
          disabled={disabled}
          onSetAll={(checked) => setDeptAll(dept, checked)}
          onToggleTool={toggleTool}
        />
      ))}
    </div>
  );
}

// ----- Add user --------------------------------------------------------------

/**
 * AddUserForm — a slide-over for adding a person who does not exist yet. Unlike
 * the GrantEditor (which searches the directory and writes per-slug grants),
 * this collects an email, an optional name, and a set of tools, then makes one
 * call (POST /v1/access/users) that provisions the grants AND emails the person
 * an invitation. Reuses ToolGrid for the checkbox grid and mirrors the
 * GrantEditor slide-over shell.
 */
export function AddUserForm({ onClose }: { onClose: () => void }) {
  const createUserM = useCreateUser();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [tools, setTools] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Escape closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trimmedEmail = email.trim();
  const emailValid = isEmail(trimmedEmail);
  const saving = createUserM.isPending;
  const canSave = emailValid && !saving && !done;

  const onSave = async () => {
    setError(null);
    if (!emailValid) {
      setError('Enter a valid email address.');
      return;
    }
    try {
      await createUserM.mutateAsync({
        email: trimmedEmail,
        name: name.trim() || undefined,
        toolSlugs: [...tools],
      });
      // The mutation invalidates the grants list on success, so the console
      // refreshes and the new person appears.
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/30" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add user"
        className="relative z-10 flex h-full w-full max-w-md flex-col bg-amp-white shadow-2 dark:bg-amp-dark-surface"
      >
        <div className="flex items-center justify-between border-b border-amp-line px-5 py-4 dark:border-amp-dark-line">
          <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">Add user</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {done ? (
            <div className="rounded border border-amp-line bg-amp-cream/40 px-4 py-3 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm/40">
              <h3 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">
                Invitation sent
              </h3>
              <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {trimmedEmail} was added and emailed an invitation. They appear in the list now.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <label
                  htmlFor="add-user-email"
                  className="block text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2"
                >
                  Email
                </label>
                <input
                  id="add-user-email"
                  type="email"
                  autoFocus
                  autoComplete="off"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="person@example.com"
                  className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
                />
              </div>

              <div className="mb-5">
                <label
                  htmlFor="add-user-name"
                  className="block text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2"
                >
                  Name <span className="normal-case font-normal">(optional)</span>
                </label>
                <input
                  id="add-user-name"
                  type="text"
                  autoComplete="off"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
                />
              </div>

              <p className="text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2">
                Tool access
              </p>
              <div className="mb-3 rounded border border-amp-line bg-amp-cream/40 px-3 py-2 text-xs text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface-warm/40 dark:text-amp-dark-ink-muted">
                A checked tool is granted. An unchecked tool is not.
              </div>

              <ToolGrid value={tools} onChange={setTools} />

              {error && (
                <div className="mt-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t border-amp-line px-5 py-4 dark:border-amp-dark-line">
          {done ? (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                className="text-sm rounded bg-amp-olive-deep px-3 py-1.5 font-medium text-amp-cream hover:bg-amp-olive dark:bg-amp-olive dark:hover:bg-amp-olive-deep"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-3">
                The person is emailed an invitation. Changes take effect at their next sign-in. All
                changes are logged.
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={!canSave}
                  className="inline-flex items-center gap-1 text-sm rounded bg-amp-olive-deep px-3 py-1.5 font-medium text-amp-cream hover:bg-amp-olive disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amp-olive dark:hover:bg-amp-olive-deep"
                >
                  {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                  Add user
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Department section ----------------------------------------------------

function DeptSection({
  dept,
  tools,
  disabled,
  onSetAll,
  onToggleTool,
}: {
  dept: GrantableDept;
  tools: Set<string>;
  disabled: boolean;
  onSetAll: (checked: boolean) => void;
  onToggleTool: (slug: string, checked: boolean) => void;
}) {
  const [open, setOpen] = useState(true);
  const listId = `grant-dept-${dept.deptId}`;
  const grantableEntries = dept.entries.filter((e) => e.grantable);
  const allChecked =
    grantableEntries.length > 0 && grantableEntries.every((e) => tools.has(e.accessSlug));

  return (
    <div className="rounded border border-amp-line dark:border-amp-dark-line">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={listId}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-amp-ink dark:text-amp-dark-ink"
        >
          <ChevronDown
            aria-hidden
            className="h-3 w-3 text-amp-ink-muted dark:text-amp-dark-ink-muted transition-transform"
            style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
          />
          {dept.deptName}
        </button>
        {grantableEntries.length > 0 && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onSetAll(!allChecked)}
            className="text-xs text-amp-olive hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-amp-olive-dark"
          >
            {allChecked ? 'Clear all' : 'Select all'}
          </button>
        )}
      </div>
      {open && (
        <div id={listId} className="border-t border-amp-line dark:border-amp-dark-line">
          {dept.entries.map((entry) => (
            <ToolRow
              key={entry.toolId}
              entry={entry}
              checked={tools.has(entry.accessSlug)}
              disabled={disabled}
              onChange={(checked) => onToggleTool(entry.accessSlug, checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ToolRow({
  entry,
  checked,
  disabled,
  onChange,
}: {
  entry: GrantableEntry;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 px-3 py-2 pl-7 cursor-pointer">
      <span className="text-sm text-amp-ink dark:text-amp-dark-ink">{entry.toolName}</span>
      {entry.grantable ? (
        <input
          type="checkbox"
          aria-label={`Access for ${entry.toolName}`}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      ) : (
        // Non-grantable tool: access is automatic for all employees, so there
        // is no checkbox to set. Read-only "Everyone" label instead, keeping
        // the tool visible in the console (mirrors the nav).
        <span
          className="min-w-[110px] text-right text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
          title="Access is automatic for all employees. Nothing to grant."
        >
          Everyone
        </span>
      )}
    </label>
  );
}
